import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Dialog, DialogContent, Divider } from '@mui/material';
import { ModalDialogActions } from '../../../../../Component/Common/Components/Modal/ModalDialogActions';
import { AuditDateDetails } from '../../../../../Component/Common/Components/Modal/AuditDateDetails';
import { useCallback, useEffect, useState } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { IndividualJustificationService } from '../../../../../Component/Api/EmploymentEquity/Deviations/IndividualJustificationService';
import { CoreStaticDataService } from '../../../../../Component/Api/StaticData/CoreStaticDataService';
import { ConfirmationDialog } from '../../../../../Component/Common/Components/ConfirmationDialog';
import AppSelectList, { IDropDownOptions } from '../../../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppSelectListWithGroups, { IDropDownOptionsGroup } from '../../../../../Component/Common/Components/FormValidationInputs/AppSelectListWithGroups';
import AppYesOrNoSelect from '../../../../../Component/Common/Components/FormValidationInputs/AppYesOrNoSelector';
import AppTextInput from '../../../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { SmallLoading } from '../../../../../Component/Common/Components/Loading/IntermediateLoading';
import { MDBox } from '../../../../../Component/Common/Components/MDBox';
import { MDTypography } from '../../../../../Component/Common/Components/MDTypography';
import { AppQuill } from '../../../../../Component/Common/Components/FormValidationInputs/AppQuill';
import { IndividualJustificationDto } from '../../../../../Component/Model/Dto/EmploymentEquity/IndividualJustification';
import { CustomDialogTitle } from '../../../../Common/Custom/CustomDialogTitle';
import { PortalDocumentsDropZone } from '../../../../Common/PortalDocuments/PortalDocumentsDropZone';
import { DeviationReason } from '../../../../../Component/Model/Enum/BEE123Enums';
import { DemographicGroupList, DemographicGroupListWithNone } from '../../../../../Component/Model/Enum/MultiSelectOptions/DemographicGroupOptions';
import { individualJustificationSchema } from './IndividualJustificationModalValidation';

interface IProps {
    open: boolean;
    scorecardId: string;
    individualEmployeeDataId: number;
    justificationId: number | null;
    employeeName: string;
    occupationalLevelDisplayName?: string;
    onClose: () => void;
    onCreated: () => void;
    onSaved: () => void;
    onDeleted: () => void;
}

const auditPositionTypeOptions: IDropDownOptions[] = [
    { id: 1, value: 'New Position' },
    { id: 2, value: 'Replacement' },
    { id: 3, value: 'Temporary' },
    { id: 4, value: 'Contract' },
];

const candidateTimeOptions: IDropDownOptions[] = [
    { id: 1, value: '1-3 Months' },
    { id: 2, value: '6-12 Months' },
    { id: 3, value: '12 Months or Longer' },
];

const demographicGroupOptions: IDropDownOptions[] = DemographicGroupList.map(o => ({ id: o.value, value: o.label }));

const demographicGroupOptionsWithNone: IDropDownOptions[] = DemographicGroupListWithNone.map(o => ({ id: o.value, value: o.label }));

const enhancedReasonIds = [
    DeviationReason.InsufficientRecruitmentOpportunities,
    DeviationReason.InsufficientPromotionOpportunities,
    DeviationReason.InsufficientTargetIndividuals,
];

const defaultFormValues = {
    reasonId: '',
    comment: '',
    remedialPlan: '',
    auditDepartmentOrDivision: '',
    auditJobTitle: '',
    auditUrgencyLevel: '',
    auditPositionTypeId: '',
    eePlanRequiresDesignatedPerson: null,
    eePlanHighestPriorityTargetGroup: '',
    eePlanSecondHighestPriorityTarget: '',
    eePlanThirdHighestPriorityTarget: '',
    eePlanIsPreferredCandidateAlignedToHighestPriority: null,
    eePlanJobProfile: '',
    eePlanChannelsJobAdvertised: '',
    eePlanCoreResponseNumbers: '',
    eePlanShortlistedCandidatesDemographics: '',
    candidateQualifications: '',
    candidatePriorLearning: '',
    candidateRelevantExperience: '',
    candidateTimeToAcquireCompetencyId: '',
    approvedByRecruitingLineManager: '',
    approvedBySeniorManagerEE: '',
    approvedByCeoOrDesignee: '',
};

const encodeDemo = (raceId: number | null | undefined, genderId: number | null | undefined): string => {
    if (!raceId || !genderId) return '';
    return `${raceId}_${genderId}`;
};

const decodeDemo = (val: any): { raceId: number | null; genderId: number | null } => {
    if (!val) return { raceId: null, genderId: null };
    const parts = String(val).split('_');
    const raceId = parseInt(parts[0]);
    const genderId = parseInt(parts[1]);
    return {
        raceId: isNaN(raceId) ? null : raceId,
        genderId: isNaN(genderId) ? null : genderId,
    };
};

export const IndividualJustificationModal = ({
    open,
    scorecardId,
    individualEmployeeDataId,
    justificationId,
    employeeName,
    occupationalLevelDisplayName,
    onClose,
    onCreated,
    onSaved,
    onDeleted,
}: IProps) => {
    const isEditMode = justificationId !== null;

    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [savedId, setSavedId] = useState<number | undefined>(isEditMode ? justificationId! : undefined);
    const [reasonGroups, setReasonGroups] = useState<IDropDownOptionsGroup[]>([]);
    const [displayOccupationalLevel, setDisplayOccupationalLevel] = useState<string>(occupationalLevelDisplayName ?? '');
    const [loadedDto, setLoadedDto] = useState<IndividualJustificationDto.DtoOut | null>(null);

    const { handleSubmit, control, reset, formState: { isSubmitting }, watch } = useForm({
        mode: 'all',
        resolver: yupResolver(individualJustificationSchema),
    });

    const watchedReasonId = Number(watch('reasonId'));
    const showEnhancedFields = enhancedReasonIds.includes(watchedReasonId);

    useEffect(() => {
        const loadDeviationReasons = async () => {
            try {
                const result = await CoreStaticDataService.getGroupedDeviationReasons();
                setReasonGroups(result.data.map(g => ({
                    group: g.group,
                    items: g.items.map(item => ({ id: item.id, value: item.name })),
                })));
            } catch (e) {
                console.log(e);
            }
        };
        loadDeviationReasons();
    }, []);

    const resetFormFromDto = useCallback((dto: IndividualJustificationDto.DtoOut) => {
        if (dto.occupationalLevel) {
            setDisplayOccupationalLevel(dto.occupationalLevel);
        }
        reset({
            reasonId: dto.reasonId,
            comment: dto.comment ?? '',
            remedialPlan: dto.remedialPlan ?? '',
            auditDepartmentOrDivision: dto.auditDepartmentOrDivision ?? '',
            auditJobTitle: dto.auditJobTitle ?? '',
            auditUrgencyLevel: dto.auditUrgencyLevel ?? '',
            auditPositionTypeId: dto.auditPositionTypeId ?? '',
            eePlanRequiresDesignatedPerson: dto.eePlanRequiresDesignatedPerson ?? null,
            eePlanHighestPriorityTargetGroup: encodeDemo(
                dto.eePlanHighestPriorityTargetGroupRaceId,
                dto.eePlanHighestPriorityTargetGroupGenderId,
            ),
            eePlanSecondHighestPriorityTarget: encodeDemo(
                dto.eePlanSecondHighestPriorityTargetRaceId,
                dto.eePlanSecondHighestPriorityTargetGenderId,
            ),
            eePlanThirdHighestPriorityTarget: encodeDemo(
                dto.eePlanThirdHighestPriorityTargetRaceId,
                dto.eePlanThirdHighestPriorityTargetGenderId,
            ),
            eePlanIsPreferredCandidateAlignedToHighestPriority: dto.eePlanIsPreferredCandidateAlignedToHighestPriority ?? null,
            eePlanJobProfile: dto.eePlanJobProfile ?? '',
            eePlanChannelsJobAdvertised: dto.eePlanChannelsJobAdvertised ?? '',
            eePlanCoreResponseNumbers: dto.eePlanCoreResponseNumbers ?? '',
            eePlanShortlistedCandidatesDemographics: dto.eePlanShortlistedCandidatesDemographics ?? '',
            candidateQualifications: dto.candidateQualifications ?? '',
            candidatePriorLearning: dto.candidatePriorLearning ?? '',
            candidateRelevantExperience: dto.candidateRelevantExperience ?? '',
            candidateTimeToAcquireCompetencyId: dto.candidateTimeToAcquireCompetencyId ?? '',
            approvedByRecruitingLineManager: dto.approvedByRecruitingLineManager ?? '',
            approvedBySeniorManagerEE: dto.approvedBySeniorManagerEE ?? '',
            approvedByCeoOrDesignee: dto.approvedByCeoOrDesignee ?? '',
        });
    }, [reset]);

    const loadJustification = useCallback(async (id: number) => {
        setIsLoading(true);
        try {
            const result = await IndividualJustificationService.Get(scorecardId, id);
            resetFormFromDto(result.data);
            setSavedId(result.data.id);
            setLoadedDto(result.data);
        } catch (e) {
            console.log(e);
        } finally {
            setIsLoading(false);
        }
    }, [scorecardId, resetFormFromDto]);

    useEffect(() => {
        if (!open) return;
        setSavedId(isEditMode ? justificationId! : undefined);
        if (isEditMode) {
            loadJustification(justificationId!);
        } else {
            reset(defaultFormValues);
            setLoadedDto(null);
        }
    }, [open, isEditMode, justificationId, loadJustification, reset]);

    const handleFormError = () => {
        const form = document.getElementById('individualJustificationForm');
        if (!form) return;
        const firstInvalid = form.querySelector<HTMLElement>('[aria-invalid="true"]');
        if (firstInvalid) {
            firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
            const dialogContent = form.closest<HTMLElement>('.MuiDialogContent-root');
            if (dialogContent) dialogContent.scrollTop = 0;
        }
    };

    const handleDataSubmit: SubmitHandler<FieldValues> = async (data) => {
        const isEnhanced = enhancedReasonIds.includes(Number(data.reasonId));
        const highestGroup = decodeDemo(data.eePlanHighestPriorityTargetGroup);
        const secondGroup = decodeDemo(data.eePlanSecondHighestPriorityTarget);
        const thirdGroup = decodeDemo(data.eePlanThirdHighestPriorityTarget);

        const dtoIn: IndividualJustificationDto.DtoIn = {
            id: savedId ?? 0,
            scorecardId,
            individualEmployeeDataId,
            reasonId: Number(data.reasonId),
            comment: data.comment ?? '',
            remedialPlan: data.remedialPlan || null,
            auditDepartmentOrDivision: isEnhanced ? (data.auditDepartmentOrDivision || null) : null,
            auditJobTitle: isEnhanced ? (data.auditJobTitle || null) : null,
            auditUrgencyLevel: isEnhanced ? (data.auditUrgencyLevel || null) : null,
            auditPositionTypeId: isEnhanced ? (Number(data.auditPositionTypeId) || null) : null,
            eePlanRequiresDesignatedPerson: isEnhanced ? (data.eePlanRequiresDesignatedPerson ?? null) : null,
            eePlanHighestPriorityTargetGroupRaceId: isEnhanced ? highestGroup.raceId : null,
            eePlanHighestPriorityTargetGroupGenderId: isEnhanced ? highestGroup.genderId : null,
            eePlanSecondHighestPriorityTargetRaceId: isEnhanced ? secondGroup.raceId : null,
            eePlanSecondHighestPriorityTargetGenderId: isEnhanced ? secondGroup.genderId : null,
            eePlanThirdHighestPriorityTargetRaceId: isEnhanced ? thirdGroup.raceId : null,
            eePlanThirdHighestPriorityTargetGenderId: isEnhanced ? thirdGroup.genderId : null,
            eePlanIsPreferredCandidateAlignedToHighestPriority: isEnhanced
                ? (data.eePlanIsPreferredCandidateAlignedToHighestPriority ?? null)
                : null,
            eePlanJobProfile: isEnhanced ? (data.eePlanJobProfile || null) : null,
            eePlanChannelsJobAdvertised: isEnhanced ? (data.eePlanChannelsJobAdvertised || null) : null,
            eePlanCoreResponseNumbers: isEnhanced ? (data.eePlanCoreResponseNumbers || null) : null,
            eePlanShortlistedCandidatesDemographics: isEnhanced
                ? (data.eePlanShortlistedCandidatesDemographics || null)
                : null,
            candidateQualifications: isEnhanced ? (data.candidateQualifications || null) : null,
            candidatePriorLearning: isEnhanced ? (data.candidatePriorLearning || null) : null,
            candidateRelevantExperience: isEnhanced ? (data.candidateRelevantExperience || null) : null,
            candidateTimeToAcquireCompetencyId: isEnhanced
                ? (Number(data.candidateTimeToAcquireCompetencyId) || null)
                : null,
            approvedByRecruitingLineManager: data.approvedByRecruitingLineManager || null,
            approvedBySeniorManagerEE: data.approvedBySeniorManagerEE || null,
            approvedByCeoOrDesignee: data.approvedByCeoOrDesignee || null,
        };

        try {
            const isUpdated = !!savedId;
            let result: Awaited<ReturnType<typeof IndividualJustificationService.Create>>;
            if (isUpdated) {
                result = await IndividualJustificationService.Update(dtoIn);
                toast.success('Justification updated successfully');
            } else {
                result = await IndividualJustificationService.Create(dtoIn);
                toast.success('Justification added successfully');
            }
            setSavedId(result.data.id);
            resetFormFromDto(result.data);
            setLoadedDto(result.data);
            if (isUpdated) {
                onSaved();
            } else {
                onCreated();
                setTimeout(() => {
                    const form = document.getElementById('individualJustificationForm');
                    const dialogContent = form?.closest<HTMLElement>('.MuiDialogContent-root');
                    if (dialogContent) {
                        dialogContent.scrollTop = dialogContent.scrollHeight;
                    }
                }, 300);
            }
        } catch (e) {
            console.log(e);
        }
    };

    const confirmDelete = async () => {
        if (!savedId) return;
        setIsDeleting(true);
        try {
            await IndividualJustificationService.Delete(scorecardId, savedId);
            toast.success('Justification deleted successfully');
            setShowDeleteDialog(false);
            onDeleted();
        } catch (e) {
            console.log(e);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth aria-labelledby='individual-justification-dialog-title'>
                <CustomDialogTitle id='individual-justification-dialog-title' onClose={onClose}>
                    {isEditMode ? 'Edit' : 'Add'} Justification — {employeeName}
                </CustomDialogTitle>
                <DialogContent>
                    {savedId === undefined && (
                        <MDTypography variant='caption' sx={{ display: 'block', mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                            You will be able to upload supporting documents after completing the form.
                        </MDTypography>
                    )}
                    {isLoading ? (
                        <SmallLoading />
                    ) : (
                        <Box component='form' id='individualJustificationForm' onSubmit={handleSubmit(handleDataSubmit, handleFormError)} sx={{ mt: 1 }}>
                            <MDBox mb={2}>
                                <AppSelectListWithGroups
                                    control={control}
                                    name='reasonId'
                                    label='Justification Reason *'
                                    groups={reasonGroups}
                                    size='small'
                                />
                            </MDBox>

                            <MDBox mb={2}>
                                <AppQuill control={control} name='comment' label='Commentary *' maxLength={6000} />
                            </MDBox>

                            <MDBox mb={2}>
                                <AppQuill control={control} name='remedialPlan' label='Remedial Plan' maxLength={1000} />
                            </MDBox>

                            {showEnhancedFields && (
                                <>
                                    <Divider />
                                    <MDTypography variant='h6' sx={{ mt: 1, mb: 2 }}>
                                        Audit and Record Questions
                                    </MDTypography>

                                    <MDBox mb={2}>
                                        <AppTextInput
                                            control={control}
                                            name='auditDepartmentOrDivision'
                                            label='Department or Division in Which the Position Will Work'
                                            size='small'
                                        />
                                    </MDBox>

                                    <MDBox mb={2}>
                                        <AppTextInput
                                            control={control}
                                            name='auditJobTitle'
                                            label='Job Title of the Position'
                                            size='small'
                                        />
                                    </MDBox>

                                    {displayOccupationalLevel && (
                                        <MDBox mb={2}>
                                            <MDTypography variant='caption' sx={{ display: 'block', mb: 0.5, color: 'text.primary' }}>
                                                Occupational Level
                                            </MDTypography>
                                            <MDBox sx={{ px: 1.5, py: 1, bgcolor: 'grey.100', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                                <MDTypography variant='body2'>{displayOccupationalLevel}</MDTypography>
                                            </MDBox>
                                            <MDTypography variant='caption' sx={{ color: 'text.secondary', fontSize: '12px' }}>
                                                Pre-selected from context
                                            </MDTypography>
                                        </MDBox>
                                    )}

                                    <MDBox mb={2}>
                                        <AppQuill control={control} name='auditUrgencyLevel' label='Urgency Level for New Appointment' maxLength={1000} />
                                    </MDBox>

                                    <MDBox mb={2}>
                                        <AppSelectList
                                            control={control}
                                            name='auditPositionTypeId'
                                            label='Position Type'
                                            items={auditPositionTypeOptions}
                                            size='small'
                                        />
                                    </MDBox>

                                    <Divider />
                                    <MDTypography variant='h6' sx={{ mt: 1, mb: 2 }}>
                                        Employment Equity Plan Questions
                                    </MDTypography>

                                    <MDBox mb={2}>
                                        <AppYesOrNoSelect
                                            control={control}
                                            name='eePlanRequiresDesignatedPerson'
                                            label='Does the Position Being Considered Require a Specific Designated Person to Progress Toward and/or Achieve the Targets?'
                                            size='small'
                                        />
                                    </MDBox>

                                    <MDBox mb={2}>
                                        <AppSelectList
                                            control={control}
                                            name='eePlanHighestPriorityTargetGroup'
                                            label='Highest Priority Target Group'
                                            items={demographicGroupOptions}
                                            size='small'
                                        />
                                    </MDBox>

                                    <MDBox mb={2}>
                                        <AppSelectList
                                            control={control}
                                            name='eePlanSecondHighestPriorityTarget'
                                            label='Second Highest Priority Target Group'
                                            items={demographicGroupOptionsWithNone}
                                            size='small'
                                        />
                                    </MDBox>

                                    <MDBox mb={2}>
                                        <AppSelectList
                                            control={control}
                                            name='eePlanThirdHighestPriorityTarget'
                                            label='Third Highest Priority Target Group'
                                            items={demographicGroupOptionsWithNone}
                                            size='small'
                                        />
                                    </MDBox>

                                    <MDBox mb={2}>
                                        <AppYesOrNoSelect
                                            control={control}
                                            name='eePlanIsPreferredCandidateAlignedToHighestPriority'
                                            label='Is the Preferred Candidate Aligned to the Highest Priority Sub-Race and Gender Group?'
                                            size='small'
                                        />
                                    </MDBox>

                                    <Divider />
                                    <MDTypography variant='h6' sx={{ mt: 1, mb: 1 }}>
                                        Justification and Assessment of the Highest Ranking Candidate From the Targeted Sub-Race and Gender Group
                                    </MDTypography>

                                    <MDBox mb={2}>
                                        <MDTypography variant='caption' sx={{ color: 'text.primary', fontStyle: 'italic' }}>
                                            You will need to substantiate why this individual was not suitable for the job.
                                        </MDTypography>
                                    </MDBox>

                                    <MDBox mb={2}>
                                        <AppQuill control={control} name='candidateQualifications' label='Qualifications' maxLength={500} />
                                    </MDBox>

                                    <MDBox mb={2}>
                                        <AppQuill control={control} name='candidatePriorLearning' label='Prior Learning' maxLength={500} />
                                    </MDBox>

                                    <MDBox mb={2}>
                                        <AppQuill control={control} name='candidateRelevantExperience' label='Relevant Experience' maxLength={500} />
                                    </MDBox>

                                    <MDBox mb={2}>
                                        <AppSelectList
                                            control={control}
                                            name='candidateTimeToAcquireCompetencyId'
                                            label='How Long Would It Take for the Candidate to Acquire the Competencies?'
                                            items={candidateTimeOptions}
                                            size='small'
                                        />
                                    </MDBox>

                                    <Divider />
                                    <MDTypography variant='h6' sx={{ mt: 1, mb: 2 }}>
                                        Employment Equity Plan Questions
                                    </MDTypography>

                                    <MDBox mb={2}>
                                        <AppQuill control={control} name='eePlanJobProfile' label='Job Profile' maxLength={500} />
                                    </MDBox>

                                    <MDBox mb={2}>
                                        <AppQuill control={control} name='eePlanChannelsJobAdvertised' label='Channels Job was Advertised' maxLength={500} />
                                    </MDBox>

                                    <MDBox mb={2}>
                                        <AppQuill control={control} name='eePlanCoreResponseNumbers' label='Core Response Numbers' maxLength={500} />
                                    </MDBox>

                                    <MDBox mb={2}>
                                        <AppQuill control={control} name='eePlanShortlistedCandidatesDemographics' label='Sub-Race, Gender and Disability Demographics of the Short-Listed Candidates' maxLength={500} />
                                    </MDBox>
                                </>
                            )}

                            <Divider />
                            <MDTypography variant='h6' sx={{ mt: 1, mb: 2 }}>
                                Approved By
                            </MDTypography>

                            <MDBox mb={2}>
                                <AppTextInput
                                    control={control}
                                    name='approvedByRecruitingLineManager'
                                    label='Recruiting Line Manager'
                                    size='small'
                                />
                            </MDBox>

                            <MDBox mb={2}>
                                <AppTextInput
                                    control={control}
                                    name='approvedBySeniorManagerEE'
                                    label='Senior Manager Responsible for EE'
                                    size='small'
                                />
                            </MDBox>

                            <MDBox mb={2}>
                                <AppTextInput
                                    control={control}
                                    name='approvedByCeoOrDesignee'
                                    label='CEO or Their Designated Appointee'
                                    size='small'
                                />
                            </MDBox>

                            {savedId !== undefined && (
                                <MDBox mt={1}>
                                    <PortalDocumentsDropZone id={savedId} link='IndividualJustification' />
                                </MDBox>
                            )}

                            {loadedDto && (
                                <AuditDateDetails
                                    heading='Justification Date Details'
                                    createdDateUTC={loadedDto.createdDateUTC}
                                    createdByName={loadedDto.createdByName}
                                    lastModifiedDateUTC={loadedDto.lastModifiedDateUTC}
                                    lastModifiedByName={loadedDto.lastModifiedByName}
                                />
                            )}
                        </Box>
                    )}
                </DialogContent>
                <ModalDialogActions
                    formId='individualJustificationForm'
                    isSubmitting={isSubmitting}
                    onCancel={onClose}
                    showDelete={savedId !== undefined}
                    onDelete={() => setShowDeleteDialog(true)}
                />
            </Dialog>

            <ConfirmationDialog
                showDialog={showDeleteDialog}
                handleAccept={confirmDelete}
                handleCancel={() => setShowDeleteDialog(false)}
                heading='Delete Justification'
                message='Are you sure you want to delete this justification and associated files?'
                isLoading={isDeleting}
            />
        </>
    );
};
